'use client';

import { FC, memo } from 'react';

import {
  ButtonsBox,
  CardDescription,
  CardHead,
  CardTitle,
  ContentWrapper,
  DescriptionBox,
  ImageWrapper,
  InfoWrapper,
  LocationWrapper,
  StyledButton,
  Wrapper,
} from './styles';
import { HousingCardStats } from '@/components/shared/HousingCardStats';
import { Typography } from '@mui/material';
import { IHousingObjectResponse, OfferType } from '@/types';

import { COLORS } from '@/constants';
import { normalizeHTML, getAddress } from '@/common';

import { useWatchlistCard, useSearchParamsCompat, writeListingNavState } from '@/hooks';
import {
  BrokerRequestTooltip,
  CopyIcon,
  GeoIcon,
  HeartIcon,
  HousingCardGallerySwiper,
} from '@/components';
import { usePathname } from 'next/navigation';

const QuickRequestModal = dynamic(() =>
  import('@/components/modals/QuickRequestModal').then((m) => ({ default: m.QuickRequestModal })),
);
const ShareModal = dynamic(() =>
  import('@/components/modals/ShareModal').then((m) => ({ default: m.ShareModal })),
);

import isEqual from 'react-fast-compare';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { HouseListCardHorizontalProject } from './HouseListCardHorizontalProject';

interface HouseListCardHorizontalProps {
  data: IHousingObjectResponse;
  id?: string;
  offerType?: OfferType;
  fromList: boolean;
  priority?: boolean;
  cardIndex?: number;
  hideBroker?: boolean;
  compact?: boolean;
  isProject?: boolean;
}

export const HouseListCardHorizontal: FC<HouseListCardHorizontalProps> = (props) => {
  const resolvedIsProject = props.isProject ?? props.data.project;
  if (resolvedIsProject) {
    return <HouseListCardHorizontalProject {...props} />;
  }
  return <HouseListCardHorizontalInner {...props} />;
};

// eslint-disable-next-line react/display-name
const HouseListCardHorizontalInner: FC<HouseListCardHorizontalProps> = memo(
  ({ data, id, fromList = false, offerType, priority, cardIndex, hideBroker, compact }) => {
    const pathname = usePathname();
    const searchParams = useSearchParamsCompat();

    const { inWatchlist, toggleWatch } = useWatchlistCard(data);

    const baseSlug = `${data.slug}__${data.id}`;

    const listPageUrl = fromList
      ? (() => {
          const params = new URLSearchParams(searchParams);
          params.delete('fromListUrl');
          params.delete('fromDetailId');
          const search = params.toString();
          return `${pathname}${search ? `?${search}` : ''}`;
        })()
      : '';

    const slugLink = baseSlug;

    const handleCardClick = () => {
      if (fromList && listPageUrl) {
        writeListingNavState(listPageUrl, data.id);
      }
    };

    const { sell } = data ? data : { sell: false };

    const isSell = offerType ? offerType === OfferType.SELL : sell;

    return (
      <Wrapper id={id} $compact={compact}>
        <ImageWrapper $compact={compact}>
          <HousingCardGallerySwiper
            id={data.id}
            imageList={data.imageList}
            link={slugLink}
            images={data?.media?.image}
            priority={priority}
            cardIndex={cardIndex}
            onLinkClick={handleCardClick}
          />
          <BrokerRequestTooltip objectId={data.id} hasInfoRequest={data?.hasInfoRequest} />
        </ImageWrapper>

        <ContentWrapper>
          <InfoWrapper>
            <CardHead>
              <Link
                href={`/housing/${slugLink}`}
                style={{ width: '100%' }}
                onClick={handleCardClick}
              >
                <CardTitle
                  fontSize={18}
                  fontWeight={600}
                  lineHeight="126%"
                  color={COLORS['primary/600']}
                >
                  {normalizeHTML(data.title)}
                </CardTitle>
              </Link>
              <LocationWrapper>
                <GeoIcon />
                <Typography
                  fontSize={12}
                  fontWeight={400}
                  lineHeight="24px"
                  textTransform={'capitalize'}
                  noWrap
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}
                >
                  {getAddress({
                    address: data.address,
                    postcode: data.postcode,
                    place: data.place,
                  })}
                </Typography>
              </LocationWrapper>
            </CardHead>

            <DescriptionBox>
              <CardDescription fontWeight={400} fontSize={14} lineHeight="145%">
                {normalizeHTML(data?.description)}
              </CardDescription>
            </DescriptionBox>

            <HousingCardStats
              prices={data.prices}
              squares={data.squares}
              otherDetails={data.otherDetails}
              isSell={isSell}
              inline
            />

            {!hideBroker &&
              (data?.brokerData?.contactEmail ||
                data?.brokerData?.officeName ||
                data?.brokerData?.name) && (
                <div style={{ marginTop: 'auto' }}>
                  <QuickRequestModal
                    objectId={data.id}
                    whatsappDisabled={data.whatsappDisabled}
                    hasInfoRequest={data.hasInfoRequest}
                    brokerData={{
                      imageFullUrl: data?.brokerData?.imageFullUrl,
                      name: data?.brokerData?.name,
                      officeName: data?.brokerData?.officeName,
                    }}
                  />
                </div>
              )}
          </InfoWrapper>
          <ButtonsBox>
            <StyledButton
              onClick={toggleWatch}
              aria-label={inWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
              sx={{
                color: `${COLORS['secondary/500']} !important`,
              }}
            >
              <HeartIcon
                isActive={inWatchlist}
                width={19}
                height={19}
                stroke="#EC5B5B"
                aria-hidden="true"
              />
            </StyledButton>

            <ShareModal
              id={data.id}
              title={data.title}
              media={data.imageFullUrl}
              slugLink={slugLink}
              trigger={
                <StyledButton aria-label="Share">
                  <CopyIcon width={19} height={19} aria-hidden="true" />
                </StyledButton>
              }
            />
          </ButtonsBox>
        </ContentWrapper>
      </Wrapper>
    );
  },
  isEqual,
);
